import React from "react";
import { Typography, Table, Button, Modal } from "antd";
import { useIntl } from "react-intl";
import { CloseOutlined, PrinterOutlined } from "@ant-design/icons";
import moment from "moment";
import { getPdfMargins } from "../../../components/util/helpers";
import { exportReportToPdf } from "../../../components/util/PDFExporter";

const { Text, Title } = Typography;

interface MasavReportItem {
  patient_first_name: string;
  patient_surename: string;
  patient_local_id_number: string;
  bank_name: string;
  bank_branch_number: string;
  bank_account_number: string;
  total_amount: number;
  error_message?: string; // Add optional error message field
}

interface GenerateMasavReportTableProps {
  items: MasavReportItem[];
  onHide: () => void;
  visible: boolean;
}

const GenerateMasavReportTable: React.FC<GenerateMasavReportTableProps> = ({
  items,
  onHide,
  visible,
}) => {
  const intl = useIntl();

  const getErrorMessage = (record: MasavReportItem) => {
    if (!record.error_message) {
      return "";
    }
    let errorId = "";
    switch (record.error_message) {
      case "patient_missing_massav_information":
        errorId =
          "app.prospectuses.masavReport.error.patient_missing_massav_information";
        break;
      case "patient_invalid_charge_date":
        errorId =
          "app.prospectuses.masavReport.error.patient_invalid_charge_date";
        break;
      default:
        return `app.prospectuses.masavReport.error.${record.error_message}`; // Return raw message for unknown errors
    }
    return intl.formatMessage({
      id: errorId,
      defaultMessage: record.error_message, // Fallback default
    });
  };

  const columns = [
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.patientFirstName",
        defaultMessage: "Patient First Name",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.patient_first_name}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.patientSurname",
        defaultMessage: "Patient Surname",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.patient_surename}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.patientId",
        defaultMessage: "Patient ID",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.patient_local_id_number}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.bankName",
        defaultMessage: "Bank Name",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.bank_name}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.bankBranch",
        defaultMessage: "Bank Branch",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.bank_branch_number}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.bankAccount",
        defaultMessage: "Bank Account",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.bank_account_number}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.totalAmount",
        defaultMessage: "Total Amount",
      }),
      render: (text: any, record: MasavReportItem) => (
        <Text>{record.total_amount}</Text>
      ),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.masavReport.errorMessage",
        defaultMessage: "Error Message",
      }),
      render: (text: any, record: MasavReportItem) => {
        if (!record.error_message) {
          return null;
        }
        return <Text type="danger">{getErrorMessage(record)}</Text>;
      },
    },
  ];

  const setHeader = (doc: any) => {
    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.setR2L(true);
    doc.setFont("Rubik", "normal"); // set font
    const title = intl.formatMessage({
      id: "app.prospectuses.masavReportTableTitle",
      defaultMessage: "Masav Report Summary",
    });
    const margins = 10;
    var hLoc = doc.internal.pageSize.getWidth() - margins;
    const report = "תאריך הדפסה";
    const date = `${moment().format("HH:mm DD/MM/YYYY")}`;
    const reportDate = `${report} - ${date}`;
    doc.text(title, hLoc, 20, {
      align: "right",
    });
    doc.setFontSize(12);

    doc.text(reportDate, margins, 20, {
      align: "left",
    });
  };

  const getItems = () => {
    return items.map((record) => ({
      error_message: getErrorMessage(record),
      total_amount: record.total_amount,
      bank_account_number: record.bank_account_number,
      bank_branch_number: record.bank_branch_number,
      bank_name: record.bank_name,
      patient_local_id_number: record.patient_local_id_number,
      patient_surename: record.patient_surename,
      patient_first_name: record.patient_first_name,
    }));
  };

  const onPrint = () => {
    const fileName = intl.formatMessage({
      id: "app.prospectuses.masavReportTableTitle",
      defaultMessage: "Masav Report Summary",
    });
    const tableData = getItems();
    const options = {
      setHeader,
      headerRecord: null, // Not needed for simple header
      intl,
      category: "masavReport", // Category for intl keys in getAutoTableData
      tableOptions: {
        styles: { fontSize: 9 },
        margin: { top: 36 },
      },
      orientation: "l", // landscape
      action: "print",
      setFooter: "paging", // Add setFooter option
    };

    exportReportToPdf(fileName, tableData, options);
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width={"fit-content"}
      style={{ top: 20, padding: 0 }}
      styles={{
        body: {
          padding: 0,
        },
      }}
      open={visible}
      destroyOnClose={true}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          className="header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 20px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Title level={3} ellipsis style={{ margin: 0, flexGrow: 1 }}>
            {intl.formatMessage({
              id: "app.prospectuses.masavReportTableTitle",
              defaultMessage: "Masav Report Summary",
            })}
          </Title>
          <Button
            className="printBtn"
            type="text"
            shape="circle"
            icon={<PrinterOutlined />}
            onClick={onPrint}
            style={{ marginRight: 8 }} // Add some space between buttons
          />
          <Button
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onHide}
          />
        </div>
        <Table
          bordered
          pagination={false}
          columns={columns}
          dataSource={items}
          rowKey={(record) => record.patient_local_id_number} // Assuming ID is unique per report
          scroll={{ y: window.innerHeight - 210 }}
        />
      </div>
    </Modal>
  );
};

export default GenerateMasavReportTable;

declare global {
  interface Window {
    jsPDF: any; // Consider using a more specific type if available
  }
}
